import React, { useState } from 'react';
import { ChartLine, PieChart as PieIcon, Target, UserCheck, X } from 'lucide-react';
import type { LeadFilters, PartnerRow } from '../../../hooks/pipelineTracker/useLeadSourceReport';
import { useSalesAnalyticsReport, type RollingPeriod } from '../../../hooks/pipelineTracker/useSalesAnalyticsReport';
import { useMovementTrendReport } from '../../../hooks/pipelineTracker/useMovementTrendReport';
import { usePipelineTargets } from '../../../hooks/pipelineTracker/usePipelineTargets';
import { DEFAULT_MIX_STAGE, dayLabel, type MixStage } from '../../../hooks/pipelineTracker/salesAnalyticsTypes';
import {
  NO_TARGET_SET,
  RAG_LABEL,
  TARGET_STAGES,
  gradePartnerStages,
  monthLabel,
  partnerTargetsFor,
  type FlowFigure,
  type MovementTrendMonth,
  type MovementTrendResponse,
  type RagThresholds,
  type StageGrade,
  type StageTargets,
  type TargetStageKey,
  type TargetScope,
  type TargetsDoc,
} from '../../../hooks/pipelineTracker/targetsContract';
import { formatCents } from '../../../hooks/pipelineTracker/usePipelineReport';
import { Honesty, Panel, Pill, money, num } from './pipelineParts';
import { HousePanel, chartColour } from './trackerKit';
import { TH, shortCount, shortMoney } from './trackerUi';
import { DealMixSection, SalesAnalyticsState } from './SalesAnalyticsSections';
import { MovementTrendView } from './MovementTrendSection';

/**
 * Leads & Partners: one referrer.
 *
 * Opened from the referrer picker on the Partners view. Shows, for the partner chosen, exactly what
 * the tab shows for everyone: the partner's row of the stage table, the deal mix wheels (the
 * Pipeline tab's component, with its stage choice) and the 12-month movement trend, each asked of
 * the SAME server reports with `partnerKey` set, so a figure here is a figure the rest of the tab
 * could reproduce. Nothing is computed from deal lists in the browser; no borrower data reaches it.
 *
 * THE PARTNER LIVES IN THE SALES EXPORT. The wheels and the trend read Performance; the referrer is
 * attributed by joining the Sales upload for the same date, by the one rule Leads & Partners uses.
 * When that upload is missing the server says `needs_sales_export`, and this view says so in words,
 * never with an empty wheel that would read as nothing settled.
 *
 * TARGETS. Referrer targets live on the targets document under `byPartner`, keyed by the same
 * partner key (workstream A, 2026-10-20). They are read through `readPartnerTargets` below and graded
 * with gradePartnerStages, the one rule the practice's RAG thresholds apply everywhere, then drawn on
 * the trend as its dashed line. No target set reads "No target set", never zero.
 */

/**
 * One referrer's targets, or undefined when none is set for it. The typed read is
 * partnerTargetsFor in targetsContract.ts (workstream A): an entry with no figure in it is not set,
 * as resolveTargetScope treats a broker's.
 */
export function readPartnerTargets(targets: TargetsDoc | null | undefined, partnerKey: string): StageTargets | undefined {
  return partnerTargetsFor(targets, partnerKey) ?? undefined;
}

/**
 * The trend's target scope for one referrer. `kind: 'broker'` is the one per-entity kind the
 * contract has today; the trend reads only `kind !== 'none'`, `stages` and `rag`, so a referrer's
 * targets draw exactly as a broker's would.
 */
export function partnerTargetScope(targets: TargetsDoc | null | undefined, partnerKey: string): TargetScope {
  const own = readPartnerTargets(targets, partnerKey);
  if (own === undefined || !targets) return { kind: 'none', message: NO_TARGET_SET };
  return { kind: 'broker', stages: own, rag: targets.rag };
}

/** The Pipeline rolling periods; Leads & Partners' 12M is a custom window to the wheels. */
const ROLLING: ReadonlySet<string> = new Set<RollingPeriod>(['wtd', 'mtd', 'qtd', 'fytd', 'cytd']);

/**
 * The wheels' window from the tab's Created during choice: a rolling period as is; 12M or custom
 * dates as the cohort's window, clipped to the Performance snapshot the wheels are read from (Sales
 * may be dated a day after it, and a window ending after the snapshot is refused by the server).
 */
export function wheelWindow(
  filters: Pick<LeadFilters, 'period' | 'windowStart' | 'windowEnd'>,
  cohort: { from: string; to: string } | null | undefined,
  comparedAsAtDate: string,
): { period?: RollingPeriod; windowStart?: string; windowEnd?: string } {
  if (filters.period !== undefined && ROLLING.has(filters.period)) return { period: filters.period as RollingPeriod };
  if (cohort) return { windowStart: cohort.from, windowEnd: cohort.to < comparedAsAtDate ? cohort.to : comparedAsAtDate };
  return {};
}

const STAGE_COLUMNS = [
  { key: 'deals', label: 'Leads' },
  { key: 'meetingBooked', label: 'Meeting booked', note: 'stage based' },
  { key: 'lodged', label: 'Lodged' },
  { key: 'approved', label: 'Approved' },
  { key: 'settled', label: 'Settled' },
] as const;

const percent = (bp: number | null | undefined): string =>
  bp == null ? '-' : `${(bp / 100).toLocaleString('en-AU', { maximumFractionDigits: 1 })}%`;

const TONE = { green: 'good', amber: 'warn', red: 'bad' } as const;

/** The plain explanatory panel for a partner filter the server cannot apply without Sales. */
export const NeedsSalesPanel: React.FC<{ title: string; asAtDate: string; icon: React.ComponentType<{ className?: string }> }> = ({ title, asAtDate, icon }) => (
  <HousePanel title={title} icon={icon} color={chartColour(1)} dataTone="needs-sales">
    <p className="text-sm text-gray-200">Needs the Sales export for {dayLabel(asAtDate)}.</p>
    <p className="mt-1 text-xs leading-relaxed text-gray-400">
      The referrer is recorded in the Sales export, not in Performance. Upload the Sales export taken on the
      same day as the Performance export of {dayLabel(asAtDate)} and this section fills in for this referrer.
      Nothing is shown rather than a figure that would read as nothing settled.
    </p>
  </HousePanel>
);

/**
 * The referrer's targets against its latest month in the trend, per stage, in # and $.
 *
 * Graded on the trend's latest month, which is a monthly flow (the thing a monthly target is set
 * for), pro rata to the snapshot date when the month is short. The cohort row above is attainment
 * on leads created in a window, which is a different population; it is never graded here.
 */
export const ReferrerTargetsPanel: React.FC<{
  targets: StageTargets | undefined;
  rag: RagThresholds | undefined;
  latestMonth: MovementTrendMonth | null;
  targetsLoaded: boolean;
}> = ({ targets, rag, latestMonth, targetsLoaded }) => {
  // The four stages of the latest month, keyed as targets are, graded once by the shared rule.
  const grades: Record<TargetStageKey, StageGrade> | null =
    targets !== undefined && latestMonth !== null && rag !== undefined
      ? gradePartnerStages(
          targets,
          Object.fromEntries(TARGET_STAGES.map((stage) => [stage.key, latestMonth[stage.flow]])) as Partial<Record<TargetStageKey, FlowFigure>>,
          { from: latestMonth.from, to: latestMonth.to },
          rag
        )
      : null;
  return (
  <Panel title="Referrer targets" icon={Target} accent={chartColour(4)}
    blurb="Monthly targets set for this referrer under Targets. Graded against the latest month of the trend below, pro rata by days when that month runs only to the snapshot date.">
    {!targetsLoaded ? (
      <p className="text-sm text-gray-400">Loading targets.</p>
    ) : targets === undefined ? (
      <p className="text-sm text-gray-400" data-testid="referrer-no-target">{NO_TARGET_SET} for this referrer.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" aria-label="Referrer targets">
          <thead className="text-xs uppercase tracking-wider text-gray-400"><tr>
            <TH>Stage</TH><TH right>Monthly target</TH><TH right>{latestMonth ? `${monthLabel(latestMonth.month)}${latestMonth.partial ? ' so far' : ''}` : 'Latest month'}</TH><TH>vs target</TH>
          </tr></thead>
          <tbody className="divide-y divide-gray-700/60">
            {TARGET_STAGES.map((stage) => {
              const own = targets[stage.key];
              const actual = latestMonth ? latestMonth[stage.flow] : null;
              const countGrade = grades?.[stage.key].count ?? null;
              const valueGrade = grades?.[stage.key].value ?? null;
              const set = own?.monthlyCount !== undefined || own?.monthlyValueCents !== undefined;
              return (
                <tr key={stage.key} className="text-gray-300" data-testid={`referrer-target-${stage.key}`}>
                  <td className="px-4 py-2">{stage.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {!set ? <span className="text-gray-500">{NO_TARGET_SET}</span> : <>
                      {own?.monthlyCount !== undefined && <span>{num(own.monthlyCount)} deals</span>}
                      {own?.monthlyCount !== undefined && own?.monthlyValueCents !== undefined && <span className="text-gray-500"> · </span>}
                      {own?.monthlyValueCents !== undefined && <span>{money(own.monthlyValueCents)}</span>}
                    </>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {actual ? <>{num(actual.count)} deals<span className="text-gray-500"> · </span>{money(actual.valueCents)}</> : <span className="text-gray-500">Trend not loaded</span>}
                  </td>
                  <td className="px-4 py-2">
                    {!set ? null : (
                      <span className="flex flex-wrap gap-1.5">
                        {countGrade && <Pill tone={TONE[countGrade.tone]}>{countGrade.pct}% of deals ({RAG_LABEL[countGrade.tone]})</Pill>}
                        {valueGrade && <Pill tone={TONE[valueGrade.tone]}>{valueGrade.pct}% of $ ({RAG_LABEL[valueGrade.tone]})</Pill>}
                        {!countGrade && !valueGrade && <span className="text-xs text-gray-500">Not gradable yet</span>}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </Panel>
  );
};

export interface ReferrerViewProps {
  companyId: string;
  /** The partner picked: its key and the Referrer Person label already shown on the tab. */
  partner: { key: string; partnerLabel: string; partnerId: string | null };
  /** The partner's row in the current cohort, or null when it has no leads in the window. */
  row: PartnerRow | null;
  /** The Performance snapshot the tab's outcomes are read from; the wheels and trend read the same one. */
  comparedAsAtDate: string;
  cohort: { from: string; to: string } | null | undefined;
  filters: LeadFilters;
  /** Whether the payload carries the V6 stage columns, as the main table decides it. */
  hasStages: boolean;
  onClose: () => void;
}

const ReferrerView: React.FC<ReferrerViewProps> = ({ companyId, partner, row, comparedAsAtDate, cohort, filters, hasStages, onClose }) => {
  const [mixStage, setMixStage] = useState<MixStage>(DEFAULT_MIX_STAGE);
  const wheels = wheelWindow(filters, cohort, comparedAsAtDate);
  const sales = useSalesAnalyticsReport({
    companyId,
    asAtDate: comparedAsAtDate,
    ...wheels,
    ...(filters.adviserId ? { adviserId: filters.adviserId } : {}),
    partnerKey: partner.key,
    mixStage,
  });
  const trend = useMovementTrendReport({
    companyId,
    asAtDate: comparedAsAtDate,
    ...(filters.adviserId ? { adviserId: filters.adviserId } : {}),
    partnerKey: partner.key,
  });
  const targetsState = usePipelineTargets(companyId);
  const partnerTargets = readPartnerTargets(targetsState.targets, partner.key);
  const scope = partnerTargetScope(targetsState.targets, partner.key);
  // Split once, so each branch below is typed for what it renders.
  const trendNeedsSales = trend.response !== null && trend.response.status === 'needs_sales_export' ? trend.response : null;
  const trendResponse: MovementTrendResponse | null = trend.response !== null && trend.response.status !== 'needs_sales_export' ? trend.response : null;
  const trendOk = trendResponse !== null && trendResponse.status === 'ok' ? trendResponse : null;
  const latestMonth = trendOk && trendOk.months.length > 0 ? trendOk.months[trendOk.months.length - 1] : null;
  const salesOk = sales.response !== null && sales.response.status === 'ok' ? sales.response : null;
  const windowLabel = wheels.period !== undefined
    ? `the ${wheels.period.toUpperCase()} window`
    : wheels.windowStart !== undefined && wheels.windowEnd !== undefined
      ? `${dayLabel(wheels.windowStart)} to ${dayLabel(wheels.windowEnd)}`
      : 'the month to date';
  const stageValue = (key: (typeof STAGE_COLUMNS)[number]['key']): number => Number(row?.[key] ?? 0);

  return (
    <div className="space-y-4" data-testid="referrer-view" data-partner-key={partner.key}>
      <HousePanel
        title={`Referrer: ${partner.partnerLabel}`}
        icon={UserCheck}
        color={chartColour(1)}
        badge={partner.partnerId ? <Pill>ID {partner.partnerId}</Pill> : null}
        actions={
          <button type="button" onClick={onClose} aria-label="Close referrer view"
            className="inline-flex items-center gap-1 rounded-md border border-gray-600 bg-gray-900/80 px-2.5 py-1 text-xs font-semibold text-gray-200 hover:bg-white/[0.06]">
            <X className="h-3.5 w-3.5" aria-hidden="true" /> All partners
          </button>
        }
        blurb={`Every figure below is for the deals this referrer sent, attributed by the same rule as the table above. Wheels and trend read the Performance export of ${dayLabel(comparedAsAtDate)} joined to its Sales export${filters.adviserId ? ', for the chosen broker' : ''}.`}
        flush
      >
        {row === null ? (
          <p className="p-5 text-sm text-gray-400">No leads from this referrer were created in the selected window. The wheels and trend below still cover its deals.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-left text-sm" aria-label="Referrer stage row">
            <thead className="text-xs uppercase tracking-wider text-gray-400"><tr>
              {hasStages ? <>
                <TH>Partner</TH>
                {STAGE_COLUMNS.map((column) => <TH key={column.key} right>{column.label}{'note' in column && <span className="block text-[0.625rem] normal-case tracking-normal text-gray-500">{column.note}</span>}</TH>)}
                <TH right>Loan value</TH><TH right>In flight</TH><TH right>Lost</TH><TH right>Unclassified</TH><TH right>Settled conversion</TH>
              </> : <>
                <TH>Partner</TH><TH right>Leads</TH><TH right>Loan value</TH><TH right>In flight</TH><TH right>Settled</TH><TH right>Lost</TH><TH right>Unclassified</TH><TH right>Settled conversion</TH>
              </>}
            </tr></thead>
            <tbody>
              <tr className="text-gray-300">
                <td className="px-4 py-2">{partner.partnerLabel}</td>
                {hasStages
                  ? STAGE_COLUMNS.map((column) => <td key={column.key} className="px-4 py-2 text-right tabular-nums">{stageValue(column.key).toLocaleString('en-AU')}</td>)
                  : <td className="px-4 py-2 text-right tabular-nums">{row.deals.toLocaleString('en-AU')}</td>}
                <td className="px-4 py-2 text-right tabular-nums">{formatCents(row.loanAmountCents)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{row.inFlight}</td>
                {!hasStages && <td className="px-4 py-2 text-right tabular-nums">{row.settled}</td>}
                <td className="px-4 py-2 text-right tabular-nums">{row.lost}</td>
                <td className="px-4 py-2 text-right tabular-nums">{row.unknown ?? '-'}</td>
                <td className="px-4 py-2 text-right tabular-nums">{percent(cohort && row.deals ? Math.round(row.settled / row.deals * 10_000) : null)}</td>
              </tr>
            </tbody>
          </table></div>
        )}
        {row !== null && (
          <p className="px-5 pb-4 pt-2 text-xs text-gray-400">
            Leads created {cohort ? `${dayLabel(cohort.from)} to ${dayLabel(cohort.to)}` : 'in this snapshot'}: {shortCount(row.deals)} leads, {shortMoney(row.loanAmountCents)} loan value, as in the table above.
          </p>
        )}
      </HousePanel>

      <ReferrerTargetsPanel
        targets={partnerTargets}
        rag={targetsState.targets?.rag}
        latestMonth={latestMonth}
        targetsLoaded={targetsState.targets !== null || targetsState.error !== null}
      />
      {targetsState.error && <Honesty>{targetsState.error}</Honesty>}

      {sales.response !== null && sales.response.status === 'needs_sales_export' ? (
        <NeedsSalesPanel title="Deal mix for this referrer" asAtDate={sales.response.asAtDate} icon={PieIcon} />
      ) : (
        <DealMixSection
          accent={chartColour(1)}
          chip={<Pill>{windowLabel}</Pill>}
          mix={salesOk?.dealMix ?? null}
          salesAsAtDate={salesOk?.sales.asAtDate ?? null}
          stage={mixStage}
          onStageChange={setMixStage}
          pending={
            <SalesAnalyticsState
              title="Deal mix"
              response={sales.response}
              loading={sales.loading}
              error={sales.error}
              onRetry={sales.refetch}
            />
          }
        />
      )}
      <Honesty>
        Deals that reached the chosen stage in {windowLabel}, among the deals this referrer sent. The cohort
        row above counts leads created in the window; a wheel counts deals that reached its stage in the
        window whenever they were created, so the two can differ.
      </Honesty>

      {trendNeedsSales !== null ? (
        <NeedsSalesPanel title="Deal movements for this referrer" asAtDate={trendNeedsSales.asAtDate} icon={ChartLine} />
      ) : (
        <MovementTrendView
          response={trendResponse}
          loading={trend.loading}
          error={trend.error}
          targets={targetsState.targets === null ? undefined : scope}
          accent={chartColour(1)}
        />
      )}
    </div>
  );
};

export default ReferrerView;
